import { Injectable } from '@angular/core';

interface MenuStorageData {
  menu: any[];
  menuId: number;
  clientType: string;
  timestamp: number;
  version: number;
}

@Injectable({
  providedIn: 'root',
})
export class MenuStorageService {
  private readonly DB_NAME = 'TapgoKioskDB';
  private readonly DB_VERSION = 1;
  private readonly STORE_NAME = 'menu';
  private readonly MENU_KEY = 'current_menu';
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;
  private isInitialized = false;
  private writeLock = false; // Prevent concurrent writes

  constructor() {
    this.initPromise = this.initializeDB();
  }

  /**
   * Initialize IndexedDB
   */
  private async initializeDB(): Promise<void> {
    if (this.isInitialized && this.db) {
      return;
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

      request.onerror = () => {
        console.error('IndexedDB initialization failed:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.isInitialized = true;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.STORE_NAME)) {
          db.createObjectStore(this.STORE_NAME);
        }
      };
    });
  }

  /**
   * Ensure DB is initialized before operations
   */
  private async ensureInitialized(): Promise<void> {
    if (!this.isInitialized || !this.db) {
      if (this.initPromise) {
        await this.initPromise;
      } else {
        this.initPromise = this.initializeDB();
        await this.initPromise;
      }
    }
  }

  /**
   * Save menu to IndexedDB (with write lock)
   */
  async saveMenu(menu: any[], menuId: number, clientType: string): Promise<void> {
    await this.ensureInitialized();

    // Wait for write lock to be released
    while (this.writeLock) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    this.writeLock = true;

    try {
      if (!this.db) {
        throw new Error('IndexedDB not initialized');
      }

      const data: MenuStorageData = {
        menu,
        menuId,
        clientType,
        timestamp: Date.now(),
        version: 1,
      };

      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([this.STORE_NAME], 'readwrite');
        const store = transaction.objectStore(this.STORE_NAME);
        const request = store.put(data, this.MENU_KEY);

        request.onsuccess = () => {
          resolve();
        };

        request.onerror = () => {
          console.error('Failed to save menu to IndexedDB:', request.error);
          reject(request.error);
        };
      });
    } finally {
      this.writeLock = false;
    }
  }

  /**
   * Load menu from IndexedDB
   */
  async loadMenu(menuId: number, clientType: string): Promise<any[] | null> {
    await this.ensureInitialized();

    if (!this.db) {
      return null;
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.STORE_NAME], 'readonly');
      const store = transaction.objectStore(this.STORE_NAME);
      const request = store.get(this.MENU_KEY);

      request.onsuccess = () => {
        const data: MenuStorageData | undefined = request.result;

        if (!data) {
          resolve(null);
          return;
        }

        // Validate stored data matches current menuId and clientType
        if (data.menuId === menuId && data.clientType === clientType) {
          // Check if data is not too old (optional: 24 hours)
          const maxAge = 24 * 60 * 60 * 1000; // 24 hours
          const isExpired = Date.now() - data.timestamp > maxAge;

          if (!isExpired) {
            resolve(data.menu);
          } else {
            // Data expired, return null to trigger API fetch
            resolve(null);
          }
        } else {
          // Menu ID or client type changed, clear old data
          this.clearMenu().catch(console.error);
          resolve(null);
        }
      };

      request.onerror = () => {
        console.error('Failed to load menu from IndexedDB:', request.error);
        resolve(null); // Return null on error to allow API fallback
      };
    });
  }

  /**
   * Clear menu from IndexedDB
   */
  async clearMenu(): Promise<void> {
    await this.ensureInitialized();

    if (!this.db) {
      return;
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(this.STORE_NAME);
      const request = store.delete(this.MENU_KEY);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        console.error('Failed to clear menu from IndexedDB:', request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Check if menu exists in IndexedDB
   */
  async hasMenu(menuId: number, clientType: string): Promise<boolean> {
    const menu = await this.loadMenu(menuId, clientType);
    return menu !== null && Array.isArray(menu) && menu.length > 0;
  }
}



